import LazyImage from "@Components/Image";
import CustomLink from "@Components/CustomLink";
import config from "@Config/index";
import CrossIcon from "@Icons/CrossIcon";
import GoldStarIcon from "@Icons/GoldStarIcon";
import getImagePath from "@Utils/getImagePath";
import {
  APP_BANNER_TEXT,
  APP_DOWNLOAD_APP_STORE_LINK,
  APP_DOWNLOAD_PLAY_STORE_LINK,
  APP_DOWNLOAD_URL_QR_NUDGE_DESKTOP,
  PAGE,
} from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import getCookie from "helpers/getCookie";
import setCookie from "helpers/setCookie";
import useCashBackValue from "hooks/useCashBackValue";
import useTranslationHook from "hooks/useTranslationHook";
import { useEffect, useState } from "react";
import wrapperHOC from "@Utils/wrapperHOC";
import styles from "./AppPromotionBannerDesktop.module.scss";

/** Desktop-only; not rendered during SSR (client-only modal-style UI). */
const SHOW_APP_BANNER_DESKTOP_COOKIE = "showAppBannerDesktop";

const AppPromotionBannerDesktop = ({ parentPage = PAGE.HOME }) => {
  /** `null` = client not ready (no SSR); `true` / `false` after cookie read. */
  const [show, setShow] = useState<boolean | null>(null);

  useEffect(() => {
    setShow(getCookie(SHOW_APP_BANNER_DESKTOP_COOKIE) !== "false");
  }, []);

  const hideBanner = () => {
    gtagEventEmitter({
      action: "app_download_nudge_dismissed",
      category: "app_download_option",
      label: parentPage,
    });
    setShow(false);
    setCookie({
      cname: SHOW_APP_BANNER_DESKTOP_COOKIE,
      cvalue: "false",
      exdays: 90,
    });
  };

  const nudgeClickHandler = () => {
    gtagEventEmitter({
      action: "app_download_nudge_clicked",
      category: "app_download_option",
      label: parentPage,
    });
  };

  const appQr = APP_DOWNLOAD_URL_QR_NUDGE_DESKTOP[parentPage];
  const cashbackValue = useCashBackValue({ page: parentPage }).displayValue;
  const { t } = useTranslationHook();

  if (show !== true) {
    return null;
  }

  return (
    <div className={styles.appBannerContainer}>
      <div
        data-testid="Close-App-Banner"
        className={styles.closeBanner}
        onClick={hideBanner}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            hideBanner();
          }
        }}
        aria-label="close"
      >
        <CrossIcon />
      </div>
      <h2 data-testid="App-cashback-message" className={styles.apbTitle}>
        {cashbackValue} {t(APP_BANNER_TEXT)}
      </h2>
      <LazyImage src={appQr} height={140} width={140} />
      <div data-testid="Amber-App-download-scanner" className={styles.scanText}>
        {t("Scan to Download App")}
      </div>
      <div className={styles.ratingDiv}>
        <GoldStarIcon />
        <span data-testid="Amber-App-rating-and-downloadscount" className={styles.ratingSpan}>
          {" "}
          4.5/5{" "}
        </span>
        | 180K+ {t("Downloads")}
      </div>
      <div className={styles.downloadFromFooter}>
        <div className={styles.downloadFromText}>{t("Download from")}</div>
        <div className={styles.downloadApps} onClick={nudgeClickHandler} role="none">
          <CustomLink
            href={APP_DOWNLOAD_APP_STORE_LINK}
            target="_blank"
            rel="noreferrer"
            isExternal
            dataTestId="Get-Amber-App-from-App-Store"
          >
            <LazyImage
              src={getImagePath(`${config.IMAGE_STATIC_URL}/images/app-store.svg`)}
              alt="app store"
              className={styles.img}
              height="20px"
              width="20px"
            />
          </CustomLink>
          <CustomLink
            href={APP_DOWNLOAD_PLAY_STORE_LINK}
            target="_blank"
            rel="noreferrer"
            isExternal
            dataTestId="Get-Amber-App-from-Play-Store"
          >
            <LazyImage
              src={getImagePath(`${config.IMAGE_STATIC_URL}/images/playstore-android.svg`)}
              alt="play store"
              className={styles.img}
              height="20px"
              width="20px"
            />
          </CustomLink>
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(AppPromotionBannerDesktop, {
  componentName: "AppPromotionBannerDesktop-Global",
  showForChina: true,
  showForMiniProgram: false,
});
